import React from 'react';
import { FormattedNumber, IntlShape } from 'react-intl';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';
import { ar as dateFnsAr } from 'date-fns/locale';

import { GetOrdersResponseSingleItem } from '../../setup/apiTypes/orders';
import { makeRelativeDateCellRenderer } from '../../utils/tableRenderers';
import { FailedAttemptsItem } from '../../components/organisms/Modal/templates/OrderSummaryModal/types';
import {
  StyledValueCell,
  StyledWhatsAppButton,
} from './styles';
import { IconWhatsApp } from '../../assets/svg/icons';
import ButtonLink from '../../components/atoms/Button/buttonLink';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import routes from '../../setup/consts/routes';

const _hashHue = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return Math.abs(h);
};

const _getInitials = (name?: string): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const FailedAvatar = ({ name }: { name: string }) => {
  const hue = _hashHue(name);
  return (
    <span style={{ width: 28, height: 28, borderRadius: '50%', background: `oklch(0.85 0.04 ${hue})`, color: `oklch(0.32 0.06 ${hue})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>
      {_getInitials(name) || '·'}
    </span>
  );
};

export const ORDER_STATUS_ALL = 'all';

export const ordersTableColumns = (
  intl: IntlShape
): ColumnDef<GetOrdersResponseSingleItem>[] => [
  {
    header: intl.formatMessage({
      id: 'orders.customerName',
      defaultMessage: 'Customer',
    }),
    accessorKey: 'name',
    cell: ({ cell: { getValue } }) => {
      const name = getValue<string>();
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
          <FailedAvatar name={name} />
          <span className='ar' style={{ direction: 'rtl', unicodeBidi: 'embed', fontWeight: 600, fontSize: 'var(--t-md)', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{name || '—'}</span>
        </div>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'orders.customerPhone',
      defaultMessage: 'Phone',
    }),
    accessorKey: 'phoneNumber',
    cell: ({ cell: { getValue } }) => {
      const phone = getValue<string>();
      if (!phone) return <span style={{ color: 'var(--muted)' }}>—</span>;
      return (
        <a
          href={`tel:${phone}`}
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'inline-block', direction: 'ltr', color: 'var(--ink)', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-sm)' }}
        >
          {phone}
        </a>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'orders.city',
      defaultMessage: 'City',
    }),
    accessorKey: 'city',
    cell: ({ cell: { getValue } }) => {
      const city = getValue<string>();
      if (!city) return <span style={{ color: 'var(--muted)' }}>—</span>;
      return <span style={{ fontSize: 'var(--t-md)', color: 'var(--ink)', textTransform: 'capitalize' }}>{city}</span>;
    },
  },
  {
    header: intl.formatMessage({
      id: 'orders.valueIQD',
      defaultMessage: 'Value (IQD)',
    }),
    accessorKey: 'value',
    cell: ({ cell: { getValue } }) => (
      <StyledValueCell>
        <FormattedNumber value={getValue() as number} />
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 400, marginLeft: 4 }}>IQD</span>
      </StyledValueCell>
    ),
  },
  {
    header: intl.formatMessage({
      id: 'failureReason',
      defaultMessage: 'Failure Reason',
    }),
    accessorKey: 'failedAttempts',
    cell: ({ cell: { getValue } }) => {
      const attempts = getValue<FailedAttemptsItem[]>() ?? [];
      const latest = attempts[attempts.length - 1];
      if (!latest) return <span style={{ color: 'var(--muted-3)' }}>—</span>;
      const label = latest.reason || latest.reasonCode || '—';
      return <Badge tone={BadgeTone.Danger} dot><span className='ar'>{label}</span></Badge>;
    },
  },
  {
    header: intl.formatMessage({
      id: 'orders.lastUpdated',
      defaultMessage: 'Last Updated',
    }),
    accessorKey: 'lastUpdatedAt',
    cell: makeRelativeDateCellRenderer(intl.locale === 'ar' ? dateFnsAr : undefined),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const phone = row.original.phoneNumber;
      const orderId = row.original.id;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }} onClick={(e) => e.stopPropagation()}>
          <StyledWhatsAppButton
            type='button'
            title='Open WhatsApp'
            aria-label='Open WhatsApp'
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                `https://api.whatsapp.com/send?phone=${phone}`,
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <IconWhatsApp />
          </StyledWhatsAppButton>
          <ButtonLink
            to={generatePath(routes.EDIT_ORDER, { orderId })}
            kind={ButtonKind.Ink}
            size={ButtonSize.Small}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
          >
            {intl.formatMessage({ id: 'failedOrders.rePlaceOrder', defaultMessage: 'Re-place' })}
          </ButtonLink>
        </div>
      );
    },
  },
];
